import { useDataFetch } from './use-data-fetch';
import { useUser } from '../context/UserContext';

// Shared interfaces
export interface CurrencyTotals {
  COP: number;
  USDT: number;
  EUR: number;
  USD: number;
}

export interface Country {
  country_id: string;
  name: string;
  paymentGateways: string[];
  currency: string;
  pointValue: number;
  status: boolean;
  created_at: string;
  createdAt: string;
  updatedAt: string;
  id: string;
}

export interface CountriesResponse {
  message: string;
  data: {
    countries: Country[];
  };
  errors: null | any;
}

export interface ActiveCurrency {
  currency: string;
  totalPoints: number;
  totalValue: number;
}

export interface DatePeriod {
  start: string;
  end: string;
}

export const useMultiCurrencyBonus = () => {
  const { getData } = useDataFetch();
  const { userData } = useUser();

  // Fetch countries data to get point values for each currency
  const {
    data: countriesData,
    isLoading: countriesLoading,
    error: countriesError
  } = getData(
    ['countries-available'],
    '/product/countries-available/all',
    {
      staleTime: 30 * 60 * 1000, // 30 minutes - countries data changes rarely
      gcTime: 60 * 60 * 1000, // 1 hour
    }
  );

  const parsedCountriesData: CountriesResponse | null = countriesData || null;

  // Create a map of currency to pointValue
  const getPointValueByCurrency = (curr: string): number => {
    if (!parsedCountriesData?.data?.countries) {
      // Fallback to user's country point value if countries data not available
      return userData?.country?.pointValue || 0;
    }
    
    const country = parsedCountriesData.data.countries.find(c => c.currency === curr);
    return country?.pointValue || userData?.country?.pointValue || 0;
  };

  // Generic function to calculate active currencies from currency totals
  const calculateActiveCurrencies = (currencyTotals: CurrencyTotals): ActiveCurrency[] => {
    const activeCurrencies: ActiveCurrency[] = [];
    
    Object.entries(currencyTotals).forEach(([curr, totalPoints]) => {
      if (totalPoints > 0) {
        const currencyPointValue = getPointValueByCurrency(curr);
        activeCurrencies.push({
          currency: curr,
          totalPoints,
          totalValue: totalPoints * currencyPointValue
        });
      }
    });
    
    return activeCurrencies;
  };

  // Generic function to calculate progress percentage
  const calculateProgressPercentage = (period: DatePeriod): number => {
    if (!period) return 0;
    
    const now = new Date();
    const start = new Date(period.start);
    const end = new Date(period.end);
    
    if (now < start) return 0;
    if (now > end) return 100;
    
    const totalDuration = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    
    return Math.round((elapsed / totalDuration) * 100);
  };

  // Generic function to calculate days remaining
  const calculateDaysRemaining = (period: DatePeriod): number => {
    if (!period) return 0;
    
    const now = new Date();
    const end = new Date(period.end);
    
    if (now > end) return 0;
    
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  };

  // Generic function to check if current period is active
  const isCurrentPeriod = (period: DatePeriod): boolean => {
    if (!period) return false;
    
    const now = new Date();
    const start = new Date(period.start);
    const end = new Date(period.end);
    
    return now >= start && now <= end;
  };

  return {
    // Countries data
    countriesData: parsedCountriesData,
    countriesLoading,
    countriesError,
    
    // Utility functions
    getPointValueByCurrency,
    calculateActiveCurrencies,
    calculateProgressPercentage,
    calculateDaysRemaining,
    isCurrentPeriod,
    
    // User data
    userCountry: userData?.country,
    userCurrency: userData?.country?.currency || 'COP',
    userPointValue: userData?.country?.pointValue || 0,
  };
};
